/**
 * One-shot admin letter download: open a blank tab on the user gesture,
 * then navigate to a freshly requested signed URL. Never cache the URL.
 */

/**
 * Open a blank tab synchronously during the user gesture and keep a usable Window
 * handle. Do not pass `noopener` — modern browsers may return null even when a tab
 * was opened. Clear `opener` immediately so the downloaded document cannot reach
 * the admin page.
 *
 * @returns {Window | null}
 */
export function openAdminProspectCampaignLetterDownloadWindow() {
  // No feature string: `noopener` (and noreferrer-implied noopener) can force a null handle.
  const downloadWindow = window.open('about:blank', '_blank');
  if (!downloadWindow) return null;
  try {
    downloadWindow.opener = null;
  } catch {
    // Best effort — some browsers restrict opener mutation.
  }
  return downloadWindow;
}

/**
 * @param {Window | null | undefined} downloadWindow
 * @param {string} url
 */
export function navigateAdminProspectCampaignLetterDownloadWindow(downloadWindow, url) {
  if (!downloadWindow || !url) return;
  downloadWindow.location.href = url;
}

/**
 * @param {Window | null | undefined} downloadWindow
 */
export function closeAdminProspectCampaignLetterDownloadWindow(downloadWindow) {
  if (!downloadWindow) return;
  try {
    downloadWindow.close();
  } catch {
    // Ignore close failures on already-closed windows.
  }
}

/**
 * @param {{
 *   inFlightRef: { current: boolean },
 *   request: { campaignId: string, mailingId: string },
 *   requestDownloadUrl: (request: { campaignId: string, mailingId: string }) => Promise<{ url?: string, expiresAt?: string, fileName?: string }>,
 *   openWindow?: typeof openAdminProspectCampaignLetterDownloadWindow,
 *   closeWindow?: typeof closeAdminProspectCampaignLetterDownloadWindow,
 *   navigateWindow?: typeof navigateAdminProspectCampaignLetterDownloadWindow,
 *   nowMs?: number,
 * }} params
 */
export async function runAdminProspectCampaignLetterDownloadAttempt({
  inFlightRef,
  request,
  requestDownloadUrl,
  openWindow = openAdminProspectCampaignLetterDownloadWindow,
  closeWindow = closeAdminProspectCampaignLetterDownloadWindow,
  navigateWindow = navigateAdminProspectCampaignLetterDownloadWindow,
  nowMs = Date.now(),
}) {
  if (inFlightRef?.current) return { outcome: 'in_flight' };

  const downloadWindow = openWindow();
  if (!downloadWindow) return { outcome: 'popup_blocked' };

  inFlightRef.current = true;
  try {
    const result = await requestDownloadUrl(request);
    const url = typeof result?.url === 'string' ? result.url : '';
    const expiresAtMs = Date.parse(result?.expiresAt);
    if (!url || !/^https:\/\//i.test(url)) {
      closeWindow(downloadWindow);
      return { outcome: 'error' };
    }
    if (!Number.isFinite(expiresAtMs) || expiresAtMs <= nowMs) {
      closeWindow(downloadWindow);
      return { outcome: 'expired' };
    }
    navigateWindow(downloadWindow, url);
    return { outcome: 'opened', fileName: result.fileName || null };
  } catch (error) {
    closeWindow(downloadWindow);
    return { outcome: 'error', error };
  } finally {
    inFlightRef.current = false;
  }
}
